import { useEffect, useState, useCallback } from 'react';
import { getCategories } from './api/nominations';
import LoadingScreen  from './components/LoadingScreen';
import StageMessage   from './components/StageMessage';
import NominationForm from './components/NominationForm';
import SuccessScreen  from './components/SuccessScreen';
import VotingPortal   from './components/VotingPortal';

function applySettings(settings) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary',    settings.primary_color    || '#1a56db');
  root.style.setProperty('--color-secondary',  settings.secondary_color  || '#6c757d');
  root.style.setProperty('--color-background', settings.background_color || '#ffffff');
  root.style.setProperty('--color-text',       settings.text_color       || '#ffffff');
  document.body.style.backgroundColor = settings.background_color || '#ffffff';
}

function setFavicon(url) {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function setMetaDescription(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const plain = (tmp.textContent || tmp.innerText || '').trim();

  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'description';
    document.head.appendChild(meta);
  }
  meta.content = plain;
}

const STAGE_LABELS = { 1: 'Nominations', 2: 'Voting' };

export default function App() {
  const [loading, setLoading]           = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [survey, setSurvey]             = useState(null);
  const [settings, setSettings]         = useState(null);
  const [categories, setCategories]     = useState([]);
  const [appError, setAppError]         = useState(null);
  const [submitted, setSubmitted]       = useState(false);
  const [nomineeName, setNomineeName]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setContentReady(false);
    setAppError(null);
    try {
      const res = await getCategories();
      const { survey, settings, categories } = res.data;
      setSurvey(survey);
      setSettings(settings);
      setCategories(categories || []);
      applySettings(settings || {});
      if (survey?.name) document.title = survey.name;
      if (survey?.display_text) setMetaDescription(survey.display_text);
    } catch (err) {
      setAppError(err.response?.status === 401 ? 'unauthorized' : 'error');
    } finally {
      setTimeout(() => {
        setLoading(false);
        requestAnimationFrame(() => setContentReady(true));
      }, 400);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (settings?.logo) setFavicon(settings.logo);
  }, [settings?.logo]);

  const handleSuccess = (name) => { setNomineeName(name); setSubmitted(true); };
  const handleReset   = () => { setSubmitted(false); setNomineeName(''); };

  const renderContent = () => {
    if (appError)            return <StageMessage stage={appError} onRetry={load} />;
    if (!survey)             return null;
    if (submitted)           return <SuccessScreen nomineeName={nomineeName} onReset={handleReset} />;
    if (survey.stage === 2)  return <VotingPortal survey={survey} settings={settings} />;
    if (survey.stage !== 1)  return <StageMessage stage={survey.stage} />;
    return <NominationForm categories={categories} onSuccess={handleSuccess} />;
  };

  const stageLabel = survey ? (STAGE_LABELS[survey.stage] ?? 'Closed') : null;

  const sidebarContent = (
    <>
      {settings?.logo ? (
        <img src={settings.logo} alt={survey?.name || 'Logo'} className="sidebar-logo" />
      ) : (
        <div className="sidebar-trophy">🏆</div>
      )}

      {survey?.display_text && (
        <p className="sidebar-desc" dangerouslySetInnerHTML={{ __html: survey.display_text }} />
      )}

      <h1 className="sidebar-title">
        {survey?.name || 'Awards'}
        {survey?.period_show && survey?.period && <span><br />{survey.period}</span>}
      </h1>

      {stageLabel && (
        <div className="sidebar-stage-wrapper">
          <div className="sidebar-stage-line" />
          <span className="sidebar-stage">{stageLabel}</span>
          <div className="sidebar-stage-line" />
        </div>
      )}

      <div className="sidebar-scroll-cue d-block d-md-none" aria-hidden="true">
        ↓
      </div>
    </>
  );

  /* ── Voting layout: full-width stacked ── */
  if (survey?.stage === 2) {
    return (
      <>
        <LoadingScreen visible={loading} />
        <div className="content-voting d-flex flex-column" style={{ opacity: contentReady ? 1 : 0 }}>
          <main className="flex-grow-1">
            <VotingPortal survey={survey} settings={settings} />
          </main>
          <footer className="text-center py-3 small credits-text">
            Powered by <a href="https://etuzo.co.ke" className="credits-link">eTuzo</a>
          </footer>
        </div>
      </>
    );
  }

  /* ── All other stages: original split-screen layout ── */
  return (
    <>
      <LoadingScreen visible={loading} />

      <div className="app-wrapper" style={{ opacity: contentReady ? 1 : 0 }}>

        <aside className="sidebar-left">
          {sidebarContent}
        </aside>

        <div className="content-right">
          <main className="flex-grow-1">
            {renderContent()}
          </main>
          <footer className="text-center py-3 small credits-text">
            Powered by <a href="https://etuzo.co.ke" className="credits-link">eTuzo</a>
          </footer>
        </div>

      </div>
    </>
  );
}
